import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Checkbox, Input } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { getSortedList, useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { CustomerSelect, getQueryFilterByCustomerId } from '@/components/CustomerSelect'
import {
  ADMIN_ROLES,
  USER_ROLE,
  useAccess,
  useIsAdmin,
  useWhiteLabelBasedAccess,
} from '@/hooks/access'
import { useSearchParamsByKey, useSortParams } from '@/hooks/searchParams'
import type { AssetLocation } from '@/resources/asset-locations'
import assetLocationResources from '@/resources/asset-locations'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { AssetLocationList } from './AssetLocationList'

export const AssetLocationsListPage: FC = () => {
  // This is used to temporarily disable the feature based on the white label
  useWhiteLabelBasedAccess(['telia'])
  useAccess([...ADMIN_ROLES, USER_ROLE.IT_USER, USER_ROLE.IT_PURCHASER])

  const isAdmin = useIsAdmin()

  const navigate = useNavigate()
  const translate = useTranslation()
  const { locale } = useAppContext()

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const [{ querySort, querySortBy }] = useSortParams()

  const [queryCustomer] = useSearchParamsByKey('company')
  const [queryShowHidden, setQueryShowHidden] = useSearchParamsByKey('show-hidden')
  const [showHiddenFilter, setShowHiddenFilter] = useState(Boolean(queryShowHidden))

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
    setShowHiddenFilter(queryShowHidden ? JSON.parse(queryShowHidden) : false)
  }, [querySearch, queryShowHidden])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const { data: locationList, isLoading } = useQuery(
    commonQueries.assetLocationList(),
    assetLocationResources.getList
  )

  const stringMatcher = (item: AssetLocation) => [
    item.name,
    item.code,
    item.department.name,
    item.customer.name,
  ]

  const locationsFiltered =
    locationList?.filter(location => {
      const isActive = !location.hidden

      const isVisible = showHiddenFilter || (!showHiddenFilter && isActive)
      const isQueryFilterMet = getQueryFilter(searchFilter, stringMatcher)(location)
      const customerFilter = getQueryFilterByCustomerId(queryCustomer, location.customer.id)

      return isQueryFilterMet && isVisible && customerFilter
    }) || []

  const locationsParsedForSorting = locationsFiltered?.map(location => ({
    ...location,
    departmentName: location.department.name,
  }))

  const sortedList = getSortedList({
    list: locationsParsedForSorting,
    querySort,
    querySortBy,
    locale,
  })

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Asset locations" />
      </Container.Header>

      {isAdmin && (
        <Button
          className="mb-4 ml-0"
          Icon={<PlusIcon />}
          onClick={() => navigate(APP_ROUTES.HARDWARE_ASSET_LOCATION_NEW)}
        >
          <Translation id="New location" />
        </Button>
      )}

      <div className="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center md:gap-4">
        <Input
          id="asset-location-search"
          value={searchFilter}
          className="sm:w-64 md:w-80"
          placeholder={translate('Search')}
          onChange={e => setSearchFilter(e.target.value)}
        />

        <CustomerSelect
          filterData={
            locationList
              ?.filter(item => item.customer.id && item.customer.name)
              .map(item => ({
                customerId: item.customer.id,
                customerName: item.customer.name,
              })) || []
          }
          isLoading={isLoading}
        />

        <Checkbox
          id="show-hidden-input"
          marginTop={0}
          marginLeft={0}
          checked={queryShowHidden ? JSON.parse(queryShowHidden) : false}
          onChange={() => {
            const updatedShowHidden = queryShowHidden
              ? JSON.parse(queryShowHidden)
                ? 'false'
                : 'true'
              : 'true'
            setQueryShowHidden(updatedShowHidden)
          }}
          label={<Translation id="Show hidden" />}
        />
      </div>

      <AssetLocationList locations={sortedList} searchFilter={searchFilter} />
    </Container>
  )
}
